/**
 * Hours Per Trade Section Component for TENDER
 * Collapsible table for tracking estimated labour hours by trade
 * Columns: Trade | Hours | Rate £/hr | Value
 * 
 * NOTE: Actual hours tracking is only on PROJECT screen.
 * This section is for estimation only.
 * 
 * DATA MODEL COMPATIBILITY:
 * The hours_per_trade array on tender records uses this structure:
 * {
 *   trade: string,           // Trade name (e.g., "Bricklayer", "Carpenter")
 *   hours_allowed: number,   // Estimated hours for this trade
 *   labour_rate: number,     // Rate per hour in £
 *   total_value: number      // Calculated: hours_allowed * labour_rate
 * }
 * 
 * This structure can be populated automatically by Claude/external processes
 * via API: PUT /api/tenders/{tender_id} with { hours_per_trade: [...] }
 * or: PUT /api/tenders/{tender_id}/hours-per-trade endpoint
 * 
 * Example JSON for automation:
 * {
 *   "hours_per_trade": [
 *     { "trade": "Bricklayer", "hours_allowed": 120, "labour_rate": 38 },
 *     { "trade": "Carpenter", "hours_allowed": 80, "labour_rate": 36 }
 *   ]
 * }
 * total_value will be calculated automatically.
 */
import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Briefcase, Edit, Save, X, Loader2, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { TRADE_OPTIONS, formatCurrency } from './constants';

export function HoursPerTradeSection({ 
  tender, 
  canEdit, 
  isArchived, 
  onSave,
  submitting 
}) {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [hoursData, setHoursData] = useState([]);

  // Stable per-row key generator — index-as-key breaks mid-list removals
  // by shifting keys and reusing the wrong DOM nodes.
  const makeRow = (row = {}) => ({
    _k: `row_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    trade: '',
    hours_allowed: '',
    labour_rate: '',
    ...row,
  });

  const startEdit = () => {
    const existingHours = tender.hours_per_trade || [];
    setHoursData(existingHours.length > 0 ? existingHours.map(makeRow) : [makeRow()]);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setHoursData([]);
  };

  const addRow = () => {
    setHoursData([...hoursData, makeRow()]);
  };

  const removeRow = (index) => {
    setHoursData(hoursData.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, value) => {
    const newData = [...hoursData];
    newData[index][field] = value;
    setHoursData(newData);
  };

  const handleSave = async () => {
    const validRows = hoursData.filter(row => row.trade && row.trade.trim());
    const formattedData = validRows.map(row => ({
      trade: row.trade,
      hours_allowed: parseFloat(row.hours_allowed) || 0,
      labour_rate: parseFloat(row.labour_rate) || 0,
      total_value: (parseFloat(row.hours_allowed) || 0) * (parseFloat(row.labour_rate) || 0)
    }));

    const success = await onSave({ hours_per_trade: formattedData });
    if (success) {
      setEditMode(false);
      setHoursData([]);
    }
  };

  const calculateTotalValue = (data) => {
    return data.reduce((sum, row) => {
      const hours = parseFloat(row.hours_allowed) || 0;
      const rate = parseFloat(row.labour_rate) || 0;
      return sum + (hours * rate);
    }, 0);
  };

  const calculateTotalHours = (data) => {
    return data.reduce((sum, row) => sum + (parseFloat(row.hours_allowed) || 0), 0);
  };

  return (
    <Card className="bg-[#2A3240] border-white/10 mt-6">
      <CardHeader 
        className="pb-3 cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Briefcase className="w-5 h-5 text-[#F5A623]" />
            Hours Per Trade
          </CardTitle>
          <div className="flex items-center gap-2">
            {tender.hours_per_trade && tender.hours_per_trade.length > 0 && (
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                {tender.hours_per_trade.length} trades
              </Badge>
            )}
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-400 text-sm">Estimated labour hours by trade</p>
            {!isArchived && canEdit && !editMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); startEdit(); }}
                className="text-gray-400 hover:text-[#F5A623] h-7"
                data-testid="edit-hours-btn"
              >
                <Edit className="w-3 h-3 mr-1" />
                {tender.hours_per_trade?.length > 0 ? 'Edit' : 'Add Hours'}
              </Button>
            )}
            {editMode && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-white h-7"
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={submitting}
                  className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-7"
                  data-testid="save-hours-btn"
                >
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />Save</>}
                </Button>
              </div>
            )}
          </div>

          {editMode ? (
            <div className="space-y-3">
              {hoursData.map((row, index) => (
                <div key={row._k || index} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    {index === 0 && <Label className="text-gray-500 text-xs">Trade</Label>}
                    <Select value={row.trade} onValueChange={(value) => updateRow(index, 'trade', value)}>
                      <SelectTrigger className="bg-[#1E2530] border-white/20 text-white h-8">
                        <SelectValue placeholder="Select trade" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#2A3240] border-white/10">
                        {TRADE_OPTIONS.map(trade => (
                          <SelectItem key={trade} value={trade} className="text-white">{trade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    {index === 0 && <Label className="text-gray-500 text-xs">Hours</Label>}
                    <Input
                      type="number"
                      value={row.hours_allowed}
                      onChange={(e) => updateRow(index, 'hours_allowed', e.target.value)}
                      className="bg-[#1E2530] border-white/20 text-white h-8"
                      placeholder="0"
                    />
                  </div>
                  <div className="col-span-2">
                    {index === 0 && <Label className="text-gray-500 text-xs">Rate £/hr</Label>}
                    <Input
                      type="number"
                      value={row.labour_rate}
                      onChange={(e) => updateRow(index, 'labour_rate', e.target.value)}
                      className="bg-[#1E2530] border-white/20 text-white h-8"
                      placeholder="0"
                    />
                  </div>
                  <div className="col-span-3">
                    {index === 0 && <Label className="text-gray-500 text-xs">Value</Label>}
                    <p className="text-[#F5A623] h-8 flex items-center text-sm font-medium">
                      {formatCurrency((parseFloat(row.hours_allowed) || 0) * (parseFloat(row.labour_rate) || 0))}
                    </p>
                  </div>
                  <div className="col-span-1">
                    {index === 0 && <Label className="text-gray-500 text-xs">&nbsp;</Label>}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRow(index)}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={addRow}
                className="border-white/20 text-gray-400 hover:text-white w-full mt-2"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Trade
              </Button>
              <div className="pt-3 border-t border-white/10 mt-3">
                <div className="flex justify-between items-center">
                  <Label className="text-gray-500 text-sm">Total Estimated Value</Label>
                  <p className="text-lg font-bold text-[#F5A623]">{formatCurrency(calculateTotalValue(hoursData))}</p>
                </div>
              </div>
            </div>
          ) : tender.hours_per_trade && tender.hours_per_trade.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-gray-500 pb-2 font-medium text-xs uppercase">Trade</th>
                    <th className="text-right text-gray-500 pb-2 font-medium text-xs uppercase">Hours</th>
                    <th className="text-right text-gray-500 pb-2 font-medium text-xs uppercase">Rate £/hr</th>
                    <th className="text-right text-gray-500 pb-2 font-medium text-xs uppercase">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {tender.hours_per_trade.map((row, index) => (
                    <tr key={`${row.trade || 'row'}-${index}`} className="border-b border-white/5">
                      <td className="py-2 text-white">{row.trade}</td>
                      <td className="py-2 text-white text-right">{row.hours_allowed}</td>
                      <td className="py-2 text-white text-right">{formatCurrency(row.labour_rate)}/hr</td>
                      <td className="py-2 text-[#F5A623] text-right font-medium">
                        {formatCurrency(row.total_value || (row.hours_allowed * row.labour_rate))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/20">
                    <td className="pt-3 text-white font-medium uppercase text-xs">Total</td>
                    <td className="pt-3 text-white text-right font-medium">
                      {calculateTotalHours(tender.hours_per_trade)}
                    </td>
                    <td className="pt-3 text-gray-500 text-right">—</td>
                    <td className="pt-3 text-[#F5A623] text-right font-bold">
                      {formatCurrency(tender.hours_per_trade.reduce((sum, r) => sum + (r.total_value || (r.hours_allowed * r.labour_rate) || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center py-4">No hours data recorded yet. Click Edit to add estimates.</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
